import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, BadgeCheck, Award, Facebook, Instagram, Linkedin, Globe, CalendarClock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TeacherLives from '../components/TeacherLives';
import SmartImage from '../components/SmartImage';
import ShareButton from '../components/ShareButton';
import PrivateLessonModal from '../components/PrivateLessonModal';
import api, { mediaUrl } from '../lib/api';

const TeacherDetail = () => {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [params] = useSearchParams();
  useEffect(() => {
    if (t && t.offers_private_lessons && t.private_lesson_status === 'approved' && params.get('book') === '1') setShowBook(true);
  }, [t, params]);

  useEffect(() => {
    setLoading(true);
    api.get(`/teachers/${slug}`).then((res) => setT(res.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="App"><Header /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div><Footer /></div>;
  if (notFound || !t) return (
    <div className="App"><Header /><div className="text-center py-32 bg-cream"><p className="text-mahogany/70">Teacher not found.</p><Link to="/teachers" className="pill-btn mt-4 inline-flex">Back to Teachers</Link></div><Footer /></div>
  );

  const s = t.social || {};
  return (
    <div className="App" data-testid="page-teacher-detail">
      <Header />
      <main className="bg-cream">
        <section className="max-w-[1000px] mx-auto px-6 lg:px-10 py-12">
          <Link to="/teachers" className="inline-flex items-center gap-2 text-mahogany/70 hover:text-mahogany mb-6" data-testid="teacher-back"><ArrowLeft className="w-4 h-4" /> All Teachers</Link>
          <div className="grid md:grid-cols-[360px_1fr] gap-10 items-start">
            <SmartImage src={mediaUrl(t.photo_url)} alt={t.name} className="rounded-3xl aspect-[3/4]" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy" data-testid="teacher-name">{t.name}</h1>
                {t.is_verified && <span className="rounded-full bg-green-600 text-white text-xs px-3 py-1 flex items-center gap-1"><BadgeCheck className="w-4 h-4" /> Verified</span>}
              </div>
              {t.role && <div className="mt-2 text-mahogany/70 uppercase tracking-wider text-sm">{t.role}</div>}
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-burgundy/80">
                {t.years_experience ? <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-rose-italic" /> {t.years_experience}+ years experience</span> : null}
                {t.neo_sannyas && <span className="rounded-full bg-rose-soft text-mahogany px-3 py-0.5 text-xs">Neo-Sannyas</span>}
              </div>
              {(t.full_bio || t.short_bio) && <p className="mt-6 text-burgundy/85 leading-relaxed whitespace-pre-line">{t.full_bio || t.short_bio}</p>}
              {t.specialties?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">{t.specialties.map((sp) => <span key={sp} className="text-sm px-3 py-1 rounded-full bg-rose-soft text-mahogany">{sp}</span>)}</div>
              )}
              {t.offers_private_lessons && t.private_lesson_status === 'approved' && (
                <div className="mt-6 rounded-3xl border border-rose/40 bg-white/70 p-5" data-testid="private-lessons-card">
                  <div className="flex items-center gap-2 text-burgundy font-serif-display text-xl"><CalendarClock className="w-5 h-5 text-rose-italic" /> Private sessions</div>
                  {(t.session_types || []).length > 0 ? (
                    <ul className="mt-2 space-y-1.5">
                      {(t.session_types || []).map((s) => (
                        <li key={s.id} className="text-sm text-mahogany/80 flex flex-wrap gap-x-2" data-testid={`teacher-session-${s.id}`}>
                          <span className="font-medium text-burgundy">{s.name}</span>
                          {s.duration_min ? <span className="text-mahogany/60">· {s.duration_min} min</span> : null}
                          <span className="text-rose-italic">· {Number(s.price || 0) > 0 ? `${(t.private_lesson_currency || 'usd').toUpperCase()} ${Number(s.price).toFixed(2)}` : 'Free'}</span>
                          {s.description ? <span className="w-full text-xs text-mahogany/60">{s.description}</span> : null}
                        </li>
                      ))}
                    </ul>
                  ) : <p className="mt-1.5 text-sm text-mahogany/70">Sessions available on request.</p>}
                  {t.private_lesson_note && <p className="mt-2 text-sm text-mahogany/70">{t.private_lesson_note}</p>}
                  {t.private_availability_note && <p className="mt-1 text-xs text-mahogany/60">Availability: {t.private_availability_note}</p>}
                  <button onClick={() => setShowBook(true)} className="pill-btn mt-3" data-testid="request-private-lesson">Request a session</button>
                </div>
              )}
              <div className="mt-6 flex gap-3">
                {s.facebook && <a href={s.facebook} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Facebook className="w-5 h-5" /></a>}
                {s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Instagram className="w-5 h-5" /></a>}
                {s.linkedin && <a href={s.linkedin} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Linkedin className="w-5 h-5" /></a>}
                {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Globe className="w-5 h-5" /></a>}
                <ShareButton title={t.name} text={`Meet ${t.name}${t.role ? ', ' + t.role : ''} at Osho Universal`} testid="share-teacher" className="ml-auto" />
              </div>
            </div>
          </div>
        </section>
        <TeacherLives slug={slug} />
      </main>
      {showBook && <PrivateLessonModal teacher={t} onClose={() => setShowBook(false)} />}
      <Footer />
    </div>
  );
};

export default TeacherDetail;
